import { useTracking } from 'react-tracking';

import { Alert, AlertAction, Button } from '@simplywallst/ui-core';

import { ValuationOptionBuildMyNarrative } from '../ValuationOptionBuildMyNarrative';

import { useValuationSelectionFormData } from './hooks/useValuationSelectionFormData';
import { NarrativesEmptyPanel } from '@/pages/Narrative/components/NarrativesEmptyPanel';
import { Fragment, useState } from 'react';
import { NarrativeCardOption } from '../NarrativeCardOption';
import { NarrativeFairValueHeader } from '../NarrativeFairValueHeader';
import { Badge, cn } from '@simplywallst/ui-core/next';

const scrollableContainerClasses = cn([
  'relative',
  'overflow-x-hidden',
  'overflow-y-auto',
  'h-full',
  'scrollbar',
]);

export interface Props {
  companyId: string;
  onClose: () => void;
  userHoldsPosition?: boolean;
}

export const ValuationSelectionForm = ({
  companyId,
  onClose,
  userHoldsPosition,
}: Props) => {
  const { Track } = useTracking();
  const { tickerSymbol, myNarrative, valuationOptions } =
    useValuationSelectionFormData({
      companyId,
    });

  const [savingError, setSavingError] = useState(false);

  const hasValuationOptions = valuationOptions.some(
    ({ narrative }) => narrative
  );

  return (
    <Track>
      <div className="relative grid h-full grid-rows-[max-content_1fr_max-content] overflow-hidden">
        <NarrativeFairValueHeader>
          Valuation for {tickerSymbol}
        </NarrativeFairValueHeader>

        <div className={scrollableContainerClasses}>
          {savingError && (
            <div className="sticky inset-x-0 top-0 z-10 min-[375px]:px-x2 lg:px-x4">
              <Alert
                aria-label="Failed to select fair value error."
                styleType="negative"
                actions={
                  <AlertAction
                    shouldDismissAlert
                    onClick={() => setSavingError(false)}
                  >
                    OK
                  </AlertAction>
                }
              >
                Failed to select fair value. Please try again.
              </Alert>
            </div>
          )}
          <div className="absolute inset-0 grid auto-rows-max gap-4 pt-4 min-[375px]:px-x2 lg:px-x4">
            {!myNarrative && (
              <ValuationOptionBuildMyNarrative
                companyId={companyId}
                userHoldsPosition={userHoldsPosition}
              />
            )}

            <h3
              className="mb-0 grid grid-cols-[max-content_max-content] items-center gap-x1 px-x1 text-xs font-strong text-soft"
              id="narratives-community-narratives-heading"
            >
              Community Narratives{' '}
              <Badge tone="default">{valuationOptions.length}</Badge>
            </h3>
            {hasValuationOptions ? (
              <div
                className="grid auto-rows-max grid-cols-1 gap-2 pb-x8 min-[640px]:grid-cols-2"
                role="list"
                aria-labelledby="narratives-community-narratives-heading"
              >
                {valuationOptions
                  .filter(({ narrative }) => narrative)
                  .map((valuationOption, index) => (
                    <Fragment key={valuationOption.narrative?.id ?? index}>
                      {valuationOption.narrative && (
                        <NarrativeCardOption
                          role="listitem"
                          valuationOption={valuationOption}
                          onError={() => setSavingError(true)}
                        />
                      )}
                    </Fragment>
                  ))}
              </div>
            ) : (
              <NarrativesEmptyPanel>
                There is no community narrative available for this company
              </NarrativesEmptyPanel>
            )}
          </div>
          <div className="sticky inset-x-0 top-[calc(100%-64px)] h-x8 bg-gradient-to-b from-p-grey-100/0 to-p-grey-100" />
        </div>

        <div className="grid grid-cols-1 justify-items-end bg-surface py-4 pr-x2 lg:px-x4">
          <Button
            styleType="louderLarge"
            onClick={onClose}
            data-cy-id="narrative-fair-value-modal-done"
          >
            Done
          </Button>
        </div>
      </div>
    </Track>
  );
};
